import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin, Award, Globe, Users } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const CorporateBlueTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#003B73';
    const { fontFamily = 'font-sans', textColor = '#334155', backgroundColor = '#f1f5f9', sectionBackgroundColor = primaryColor, imageBorderColor = 'rgba(255,255,255,0.2)' } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '123-456-7890' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'hello@reallygreatsite.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? '123 Anywhere St., Any City' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce consequat orci in enim suscipit, ac ultricies elit iaculis. Sed lobortis odio eget fringilla. Sed euam nunc, pellentesque non arcu eget, sagittis sodales arcu. Vestibulum aliquot lorem et dolor tincidunt, ac tempus neque sollicitudin, commodo enim non, accumsan magna.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders
        ? 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop'
        : '');

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex overflow-hidden ${fontFamily}`} style={{ backgroundColor: backgroundColor }}>
            {/* Left Sidebar - Navy Blue */}
            <div data-cv-sidebar className="w-[35%] text-white p-10 flex flex-col rounded-r-3xl overflow-hidden" style={{ backgroundColor: sectionBackgroundColor }}>
                {isContinuationPage ? (
                    <div className="mt-4">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <>
                {/* Profile Photo */}
                {(photoSrc || showPlaceholders) && (
                <div className="mb-5 flex justify-center">
                    <div className="w-48 h-48 rounded-full border-8 overflow-hidden shadow-2xl bg-slate-300" style={{ borderColor: imageBorderColor }}>
                        {photoSrc ? (
                            <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                        ) : null}
                    </div>
                </div>
                )}

                {/* Contact Me Button Section */}
                {(phone || email || address || showPlaceholders) && (
                <section className="mb-8">
                    <div className="bg-white px-6 py-3 rounded-full text-center mb-6" style={{ color: primaryColor }}>
                        <h2 className="text-sm font-black uppercase tracking-[0.15em]">{CV_LABELS.contact}</h2>
                    </div>
                    <div className="cv-fs-contact space-y-4">
                        {phone && (
                        <div className="flex items-center gap-3">
                            <Phone className="w-5 h-5" style={{ color: primaryColor, opacity: 0.7 }} />
                            <span className="text-sm font-medium">{phone}</span>
                        </div>
                        )}
                        {email && (
                        <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5" style={{ color: primaryColor, opacity: 0.7 }} />
                            <span className="text-sm font-medium break-all">{email}</span>
                        </div>
                        )}
                        {address && (
                        <div className="flex items-center gap-3">
                            <MapPin className="w-5 h-5" style={{ color: primaryColor, opacity: 0.7 }} />
                            <span className="text-sm font-medium">{address}</span>
                        </div>
                        )}
                    </div>
                </section>
                )}

                {/* Skills Button Section */}
                <section className="mb-8">
                    <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="pill" />
                    <ul className="cv-fs-skills space-y-2 pl-4">
                        {(data.skills && data.skills.length > 0 ? data.skills : showPlaceholders ? [
                            { name: 'Project Management' },
                            { name: 'Public Relations' },
                            { name: 'Teamwork' },
                            { name: 'Time Management' },
                            { name: 'Leadership' },
                            { name: 'Effective Communication' },
                            { name: 'Critical Thinking' }
                        ] : []).map((skill, i) => (
                            <li
                                key={'id' in skill ? skill.id || i : skill.name || i}
                                className="flex items-center gap-3 text-sm font-medium"
                                data-cv-block
                                data-cv-item-id={'id' in skill ? skill.id || undefined : undefined}
                            >
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor, opacity: 0.7 }}></div>
                                {skill.name}
                            </li>
                        ))}
                    </ul>
                </section>

                {/* References Button Section */}
                <section className="mb-8">
                    <div className="bg-white px-6 py-3 rounded-full text-center mb-6" style={{ color: primaryColor }}>
                        <h2 className="text-sm font-black uppercase tracking-[0.15em]">{CV_LABELS.references}</h2>
                    </div>
                    <div className="cv-fs-references space-y-6">
                        {(data.references && data.references.length > 0 ? data.references.slice(0, 2) : showPlaceholders ? [
                            { name: 'Harumi Kobayashi', position: 'CEO', company: 'Wardiere Inc.', phone: '123-456-7890', email: 'hello@reallygreatsite.com' },
                            { name: 'Bailey Dupont', position: 'CEO', company: 'Wardiere Inc.', phone: '123-456-7890', email: 'hello@reallygreatsite.com' }
                        ] : []).map((ref, i) => (
                            <div
                                key={ref.id || i}
                                data-cv-block
                                data-cv-item-id={ref.id || undefined}
                            >
                                <p className="text-base font-black mb-1">{ref.name}</p>
                                <p className="text-xs mb-2" style={{ color: primaryColor, opacity: 0.8 }}>{ref.company} / {ref.position}</p>
                                <p className="text-xs mb-1"><span className="font-bold">Phone:</span> {ref.phone}</p>
                                <p className="text-xs"><span className="font-bold">Email:</span> {ref.email}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Language Button Section */}
                <section>
                    <SectionHeading title={CV_LABELS.languages} isContinuation={langContinued} primaryColor={primaryColor} variant="pill" />
                    <ul className="cv-fs-languages space-y-3 pl-4">
                        {(data.languages && data.languages.length > 0 ? data.languages : showPlaceholders ? [
                            { name: 'English' },
                            { name: 'French' },
                            { name: 'Spanish' }
                        ] : []).map((lang, i) => (
                            <li
                                key={'id' in lang ? lang.id || i : lang.name || i}
                                className="flex items-center gap-3 text-sm font-medium"
                                data-cv-block
                                data-cv-item-id={'id' in lang ? lang.id || undefined : undefined}
                            >
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor, opacity: 0.7 }}></div>
                                {lang.name}
                            </li>
                        ))}
                    </ul>
                </section>
                    </>
                )}
            </div>

            <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-12" style={{ backgroundColor: backgroundColor, color: textColor }}>

                {isContinuationPage ? (
                    <div className="mb-4 pb-2 border-b border-slate-200">
                        <p className="text-sm font-bold uppercase tracking-widest text-slate-400">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                <header className="mb-6">
                    <h1 className="cv-fs-name text-6xl font-black uppercase leading-none mb-3" style={{ color: primaryColor }}>
                        {data.personalInfo.firstName || (showPlaceholders ? 'FRANCISCO' : '')}<br />
                        {data.personalInfo.lastName || (showPlaceholders ? 'ANDRADE' : '')}
                    </h1>
                    <p className="cv-fs-profession text-xl text-slate-700 font-bold">
                        {data.personalInfo.profession || (showPlaceholders ? 'Marketing Manager' : '')}
                    </p>
                </header>
                )}

                {isContinuationPage && data.skills && data.skills.length > 0 && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-skills grid grid-cols-3 gap-4">
                            {data.skills.map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[8px] font-bold shrink-0" style={{ borderColor: primaryColor, color: primaryColor }}>
                                        {Math.round(skill.level || 50)}%
                                    </div>
                                    <span className="text-xs font-bold break-words" style={{ color: textColor }}>{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* About Me Section */}
                {summary && (
                <section className="mb-5">
                    <div className="inline-block text-white px-8 py-3 rounded-full mb-6" style={{ backgroundColor: primaryColor }}>
                        <h2 className="text-sm font-black uppercase tracking-[0.15em]">{CV_LABELS.about}</h2>
                    </div>
                    <p className="cv-fs-summary text-sm text-slate-700 leading-relaxed">
                        {summary}
                    </p>
                </section>
                )}

                {/* Experience Section */}
                <section className="mb-5">
                    <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="pill" />
                    <div className="cv-fs-experience space-y-5">
                        {data.experience.length > 0 ? data.experience.map((exp, i) => (
                            <div
                                key={exp.id || i}
                                data-cv-block
                                data-cv-item-id={exp.id || undefined}
                            >
                                <div className="flex justify-between items-baseline mb-2">
                                    <h3 className="text-lg font-black" style={{ color: primaryColor }}>{exp.position}</h3>
                                    <span className="text-sm font-bold text-slate-500">{exp.startDate} – {frPresent(exp.isCurrent, exp.endDate)}</span>
                                </div>
                                <p className="text-sm font-bold opacity-80 mb-3" style={{ color: textColor }}>{exp.company}</p>
                                <p className="text-sm leading-relaxed" style={{ color: textColor }}>
                                    {exp.description || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce consequat orci in enim suscipit, ac ultricies elit iaculis. Sed lobortis odio eget fringilla. ercu nunc, pellentesque non arcu eget, sagittis sodales arcu. Vestibulum aliquot lorem et dolor tincidunt, ac tempus neque sollicitudin, commodo enim non, accumsan magna.'}
                                </p>

                            </div>
                        )) : showPlaceholders ? (
                            <>
                                <div>
                                    <div className="flex justify-between items-baseline mb-2">
                                        <h3 className="text-lg font-black" style={{ color: primaryColor }}>Marketing Manager</h3>
                                        <span className="text-sm font-bold text-slate-500">2020 – 2023</span>
                                    </div>
                                    <p className="text-sm font-bold opacity-80 mb-3" style={{ color: textColor }}>Arrowai Industries</p>
                                    <p className="text-sm leading-relaxed" style={{ color: textColor }}>
                                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce consequat orci in enim suscipit, ac ultricies elit iaculis. Sed lobortis odio eget fringilla.
                                    </p>
                                </div>
                                <div>
                                    <div className="flex justify-between items-baseline mb-2">
                                        <h3 className="text-lg font-black" style={{ color: primaryColor }}>Marketing Manager</h3>
                                        <span className="text-sm font-bold text-slate-500">2019 - 2020</span>
                                    </div>
                                    <p className="text-sm font-bold opacity-80 mb-3" style={{ color: textColor }}>Ginyard International Co.</p>
                                    <p className="text-sm leading-relaxed" style={{ color: textColor }}>
                                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce consequat orci in enim suscipit.
                                    </p>
                                </div>
                            </>
                        ) : null}
                    </div>
                </section>

                {/* Education Section */}
                <section>
                    <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="pill" />
                    <div className="cv-fs-education space-y-5">
                        {data.education.length > 0 ? data.education.map((edu, i) => (
                            <div
                                key={edu.id || i}
                                data-cv-block
                                data-cv-item-id={edu.id || undefined}
                            >
                                <div className="flex justify-between items-baseline mb-2">
                                    <h3 className="text-base font-black" style={{ color: primaryColor }}>{edu.institution}</h3>
                                    <span className="text-sm font-bold text-slate-500">{edu.startDate} – {frPresent(edu.isCurrent, edu.endDate)}</span>
                                </div>
                                <p className="text-sm font-bold opacity-80" style={{ color: textColor }}>{edu.degree} of {edu.field}</p>
                                {edu.description && (
                                    <p className="text-sm leading-relaxed mt-2" style={{ color: textColor }}>{edu.description}</p>
                                )}
                            </div>
                        )) : showPlaceholders ? (
                            <>
                                <div>
                                    <div className="flex justify-between items-baseline mb-2">
                                        <h3 className="text-base font-black" style={{ color: primaryColor }}>Borcelle Business School</h3>
                                        <span className="text-sm font-bold text-slate-500">2020 - 2023</span>
                                    </div>
                                    <p className="text-sm font-bold text-slate-600">Bachelor of Business Management</p>
                                </div>
                                <div>
                                    <div className="flex justify-between items-baseline mb-2">
                                        <h3 className="text-base font-black" style={{ color: primaryColor }}>Borcelle Business School</h3>
                                        <span className="text-sm font-bold text-slate-500">2016 - 2020</span>
                                    </div>
                                    <p className="text-sm font-bold text-slate-600">Bachelor of Business Management</p>
                                </div>
                            </>
                        ) : null}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default CorporateBlueTemplate;
